import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import useNoChangeWarning from '@acorn-portal/shared/src/hooks/useNoChangeWarning';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import useGetOrCreateMidTermAdjustment, {
	handleChangeOfUsageOrMilageRating,
} from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { checkFormConditions } from '@acorn-portal/shared/src/pages/mta/utils';
import { MtaChangeType } from '@acorn-portal/shared/src/services/api';
import { updateMidTermAdjustmentDetails } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { setMidTermAdjustmentRatingResponse } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { isTelematics, isUsedForBusiness } from '@acorn-portal/shared/src/utils';
import getClosestMileage from '@acorn-portal/shared/src/utils/getClosestMileage';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Box, Collapse } from '@mui/material';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import AnnualMileageCalculator from '@acorn-portal/shared/src/components/calculators/AnnualMileageCalculator';
import {
	ANNUAL_MILEAGE,
	ANNUAL_MILEAGE_YD,
	BUSINESS_MILEAGE,
	COVER_LEVEL,
	VEHICLE_USED_FOR,
} from '@acorn-portal/shared/src/utils/modalText';
import useGetMileageOptions from '../../hooks/useGetMileageOptions';
import { useMileageModalHelperText } from '../../hooks/useMileageModalHelperText';
import ChangeOfUsageOrMileageSchema from '../../utils/validation/ChangeOfUsageOrMileageSchema';
import { getPCCVUsageOptions } from '@acorn-portal/shared/src/utils/get-pc-cv-usage-options';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

export interface IChangeUsageOrMileageForm {
	vehicleUse: string;
	levelOfCover: string;
	annualMileage: string;
	businessMileage?: string;
	licencingAuthority?: string;
	operatingArea?: string;
}

const ChangeUsageOrMileage = () => {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const [hasChanged] = useNoChangeWarning();
	const activePolicyDetails = useAppSelector((redux) => redux.midTermAdjustmentAPI);
	const lists = useAppSelector((redux) => redux.referenceData);
	const mtaChanges = useAppSelector((redux) => redux.midTermAdjustment);
	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const [userCustomMileage, setUserCustomMileage] = useState<{
		annualMileage: string;
		businessMileage: string;
	} | null>(null);
	const { annualMileageOptions } = useGetMileageOptions();
	const { discardLoading } = useDiscardMta();
	const { track } = useTrack();

	const { midTermAdjustment } = useGetOrCreateMidTermAdjustment({
		changeType: MtaChangeType.CHANGE_USAGE_AND_MILEAGE,
		setAdjustmentLoading: setLoading,
	});

	const isMyPolicy = import.meta.env.VITE_AUTH_REALM === 'MyPolicy';
	const isTelematicsOrMyPolicy = isTelematics(fullPolicy.product) || isMyPolicy;

	useEffect(() => {
		if (!mtaChanges.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
	}, []);

	useEffect(() => {
		track(`Change of Usage or Mileage Page Viewed`);
	}, []);

	const initialValues = {
		vehicleUse: midTermAdjustment.vehicleDetails.vehicleUse,
		levelOfCover: midTermAdjustment.vehicleDetails.coverDefinitionCode ?? '',
		annualMileage: getClosestMileage(annualMileageOptions, midTermAdjustment.vehicleDetails.annualMileage),
		businessMileage: getClosestMileage(annualMileageOptions, midTermAdjustment.vehicleDetails.businessMileage),
	};

	const mileageModalHelperText = useMileageModalHelperText();

	const onSubmit = async (
		values: IChangeUsageOrMileageForm,
		formikHelpers: FormikHelpers<IChangeUsageOrMileageForm>,
	): Promise<void> => {
		if (
			isUsedForBusiness(values.vehicleUse) &&
			(values.businessMileage === undefined || values.businessMileage.length === 0 || values.businessMileage === '0')
		) {
			formikHelpers.setFieldError('businessMileage', 'Please provide business mileage');
			formikHelpers.setFieldTouched('businessMileage', true, false);
			return;
		}

		const newMTAChanges = values;

		const activePolicyValues = {
			vehicleUse: activePolicyDetails.vehicleDetails.vehicleUse,
			levelOfCover: activePolicyDetails.vehicleDetails.coverDefinitionCode ?? '',
			annualMileage: getClosestMileage(annualMileageOptions, activePolicyDetails.vehicleDetails.annualMileage),
			businessMileage: getClosestMileage(annualMileageOptions, activePolicyDetails.vehicleDetails.businessMileage),
		};

		const { updateSection, continueToRate, navigateToSummary } = checkFormConditions(
			hasChanged(activePolicyValues, newMTAChanges),
			hasChanged(initialValues, newMTAChanges),
			mtaResponse,
			mtaChanges.changeOfUsage,
			location.state?.drivers,
		);

		if (!continueToRate && !navigateToSummary) {
			return;
		}
		if (!continueToRate && navigateToSummary) {
			navigate(ROUTE_PATHS.makeAChangeSummary);
			return;
		}
		setLoading(true);
		try {
			const result = await handleChangeOfUsageOrMilageRating({
				midTermAdjustment: {
					...midTermAdjustment,
					drivers: location.state?.drivers || midTermAdjustment.drivers,
					changeOfUsage: updateSection,
					changeOfDriver: location.state?.drivers ? true : false,
					changeType: [
						...midTermAdjustment.changeType,
						...(location.state?.drivers ? [MtaChangeType.CHANGE_DRIVER] : []),
					],
					vehicleDetails: midTermAdjustment.vehicleDetails,
				},
				values,
				product: fullPolicy.product,
			});

			const updatedValues = {
				...midTermAdjustment,
				changeOfUsage: updateSection,
				changeOfDriver: midTermAdjustment.changeOfDriver || location.state?.drivers ? true : false,
				drivers: location.state?.drivers || midTermAdjustment.drivers,
				changeType: [],
				vehicleDetails: {
					...midTermAdjustment.vehicleDetails,
					annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
					businessMileage: values.businessMileage ? parseInt(values.businessMileage.toString(), 10) : 0,
					coverDefinitionCode: values.levelOfCover,
					vehicleUse: values.vehicleUse,
				},
			};
			dispatch(updateMidTermAdjustmentDetails(updatedValues));
			if (!result) {
				throw new Error('No result returned from rating');
			}
			if (result.hasDeclined) {
				navigate(ROUTE_PATHS.makeAChangeReferral);
				return;
			}
			dispatch(setMidTermAdjustmentRatingResponse(result));
			setLoading(false);
			navigate(ROUTE_PATHS.makeAChangeSummary);
		} catch (error) {
			console.error('Error during change of usage or mileage:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	const pageTitle = isTelematicsOrMyPolicy ? 'Change of Usage' : 'Change of Usage and Mileage';

	// Filter vehicle options to exclude taxi usage codes for PC and CV policies.
	// Note - Taxi has it's own usage component
	const vehicleOptions = useMemo(() => getPCCVUsageOptions(lists.vehicleUseOptions), [lists.vehicleUseOptions]);

	return (
		<JourneyCardLayout pageTitle={pageTitle} loading={loading || discardLoading}>
			<Formik<IChangeUsageOrMileageForm>
				key={JSON.stringify(midTermAdjustment.transactionId)}
				validationSchema={ChangeOfUsageOrMileageSchema(fullPolicy)}
				initialValues={initialValues}
				onSubmit={onSubmit}
				onReset={() => navigate(-1)}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						<ToggleButtonFormField
							label='What will you be using the vehicle for?'
							name='vehicleUse'
							id='vehicleUse'
							value={formik.values.vehicleUse}
							onChange={(value) => {
								formik.setFieldValue('vehicleUse', value);
								if (isTelematicsOrMyPolicy && ['01', '4'].includes(value)) {
									// Store current business mileage if user has modified it
									if (
										formik.values.businessMileage &&
										formik.values.businessMileage !== initialValues.businessMileage
									) {
										setUserCustomMileage((prev) => ({
											...prev,
											businessMileage: formik.values.businessMileage || '0',
											annualMileage: initialValues.annualMileage,
										}));
									}
									// Set to 0 for non-business use
									formik.setFieldValue('businessMileage', 0);
									formik.setFieldValue('annualMileage', initialValues.annualMileage);
									return;
								}

								// Restore user's custom value if they had set one, otherwise use initial value
								const businessMileageToRestore = userCustomMileage?.businessMileage || initialValues.businessMileage;
								const annualMileageToRestore = userCustomMileage?.annualMileage || initialValues.annualMileage;

								formik.setFieldValue('annualMileage', annualMileageToRestore);
								formik.setFieldValue('businessMileage', businessMileageToRestore);
							}}
							options={vehicleOptions as IOptions[]}
							error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
							helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
							modal={{
								title: VEHICLE_USED_FOR.header,
								description: VEHICLE_USED_FOR.text,
							}}
						/>
						<Collapse
							in={!isTelematicsOrMyPolicy || (isTelematicsOrMyPolicy && isUsedForBusiness(formik.values.vehicleUse))}
						>
							<Box>
								<SelectFormField
									label='Annual mileage'
									name='annualMileage'
									id='annualMileage'
									value={formik.values.annualMileage}
									onChange={(value) => {
										formik.setFieldValue('annualMileage', value);

										if (value !== initialValues.annualMileage) {
											setUserCustomMileage((prev) => ({
												...prev,
												annualMileage: value,
												businessMileage: initialValues.businessMileage,
											}));
										}
									}}
									options={annualMileageOptions}
									modal={{
										title: ANNUAL_MILEAGE.header,
										description: !isTelematics(fullPolicy.product) ? ANNUAL_MILEAGE.text : ANNUAL_MILEAGE_YD.text,
									}}
									error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
									helperText={formik.touched.annualMileage && formik.errors.annualMileage}
								/>
								{isTelematics(fullPolicy.product) && (
									<Box
										sx={{
											position: 'relative',
											top: '-22px',
										}}
									>
										<AnnualMileageCalculator
											miles={parseInt(formik.values.annualMileage, 10)}
											id='annualMileageCalculator'
											helperText={mileageModalHelperText}
										/>
									</Box>
								)}
								<Collapse in={isUsedForBusiness(formik.values.vehicleUse)}>
									<Box>
										<BusinessMileageDropdown
											formik={formik}
											initialValues={initialValues}
											setUserCustomMileage={setUserCustomMileage}
										/>
									</Box>
								</Collapse>
							</Box>
						</Collapse>
						{activePolicyDetails.vehicleDetails.coverDefinitionCode === 'COMP' &&
							!isMyPolicy &&
							isTelematics(fullPolicy.product) && (
								<SelectFormField
									label='What level of cover do you require?'
									name='levelOfCover'
									id='levelOfCover'
									value={formik.values.levelOfCover}
									onChange={(value) => formik.setFieldValue('levelOfCover', value)}
									options={lists.levelOfCoverTypes.filter((x) => x.value !== 'TPO') as IOptions[]}
									error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
									helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
									modal={{
										title: COVER_LEVEL(fullPolicy.startDate, fullPolicy.product).header,
										description: COVER_LEVEL(fullPolicy.startDate, fullPolicy.product).text,
									}}
								/>
							)}
						<ActionButtons>
							<BackButton onClick={() => setOpen(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
						<HelperModal
							open={open}
							onClose={() => {
								setOpen(false);
							}}
							title='Discard The Changes?'
							description='Are you sure you wish to discard the changes?'
							closeText='No'
							submitText='Yes'
							onSubmit={() => {
								formik.resetForm();
							}}
						/>
					</Form>
				)}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeUsageOrMileage;

const BusinessMileageDropdown = ({
	formik,
	initialValues,
	setUserCustomMileage,
}: {
	formik: FormikProps<IChangeUsageOrMileageForm>;
	initialValues: {
		businessMileage: string;
		annualMileage: string;
	};
	setUserCustomMileage: React.Dispatch<
		React.SetStateAction<{
			annualMileage: string;
			businessMileage: string;
		} | null>
	>;
}) => {
	const { getBusinessMileageOptions } = useGetMileageOptions();

	const businessMileageOptions = useMemo(() => {
		const options = getBusinessMileageOptions(formik.values.annualMileage);
		if (formik.values.businessMileage && formik.values.annualMileage < formik.values.businessMileage) {
			formik.setFieldValue('businessMileage', initialValues.businessMileage);
		}
		return options;
	}, [formik.values.annualMileage, getBusinessMileageOptions]);

	return (
		<SelectFormField
			label='Business mileage'
			name='businessMileage'
			id='businessMileage'
			value={formik.values.businessMileage}
			onChange={(value) => {
				formik.setFieldValue('businessMileage', value);

				if (value !== initialValues.businessMileage) {
					setUserCustomMileage((prev) => ({
						...prev,
						businessMileage: value,
						annualMileage: initialValues.annualMileage,
					}));
				}
			}}
			options={businessMileageOptions}
			modal={{
				title: BUSINESS_MILEAGE.header,
				description: BUSINESS_MILEAGE.text,
			}}
			error={formik.touched.businessMileage && Boolean(formik.errors.businessMileage)}
			helperText={formik.touched.businessMileage && formik.errors.businessMileage}
		/>
	);
};
